import { useRef } from "react";
import { motion, useScroll, useTransform, useReducedMotion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { BOOK } from "../../data/content";
import { MaskLine } from "../../lib/motion";
import { BuyButton, Ornament } from "./ui";
import { trackCircleCta } from "../../lib/trackCircleCta";

export function Hero() {
  const ref = useRef(null);
  const reduce = useReducedMotion();
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const coverY = useTransform(scrollYProgress, [0, 1], [0, 120]);
  const textY = useTransform(scrollYProgress, [0, 1], [0, 60]);
  const coverOpacity = useTransform(scrollYProgress, [0, 0.8], [1, 0.2]);

  return (
    <section
      ref={ref}
      id="top"
      className="relative min-h-[100svh] hero-vignette overflow-hidden pt-24 pb-16 sm:pt-28"
      data-testid="hero"
      data-section="hero"
      data-section-label="Hero"
    >
      {/* ambient gold glow */}
      <div
        className="absolute top-1/4 left-1/2 -translate-x-1/2 w-[80vw] h-[60vh] rounded-full blur-[120px] opacity-[0.14] pointer-events-none"
        style={{ background: "radial-gradient(circle, #aa8656 0%, transparent 70%)" }}
      />

      <div className="relative z-10 max-w-7xl mx-auto px-5 sm:px-10 grid lg:grid-cols-12 gap-12 lg:gap-8 items-center min-h-[calc(100svh-11rem)]">
        {/* Text */}
        <motion.div style={reduce ? {} : { y: textY }} className="lg:col-span-7 order-2 lg:order-1">
          <MaskLine delay={0.1}>
            <span className="label">A New Frontier in Psychodrama, Trauma &amp; Embodied Memory</span>
          </MaskLine>

          <h1 className="font-display text-cream mt-6 leading-[0.95]">
            <MaskLine delay={0.25} className="text-5xl sm:text-6xl lg:text-7xl gold-gradient-text">
              SELF
            </MaskLine>
            <MaskLine delay={0.4} className="text-5xl sm:text-6xl lg:text-7xl gold-gradient-text">
              DOUBLING<span className="align-super text-2xl sm:text-3xl">©</span>
            </MaskLine>
          </h1>

          <MaskLine delay={0.6} className="mt-5">
            <span className="font-editorial italic text-2xl sm:text-3xl text-secondary">
              {BOOK.subtitle}
            </span>
          </MaskLine>

          <Ornament className="my-8 max-w-md !justify-start" />

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.85, duration: 0.9 }}
            className="font-editorial italic text-secondary text-lg sm:text-xl lg:text-2xl leading-snug max-w-xl"
          >
            {BOOK.heroText}
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.2, duration: 0.8 }}
            className="flex items-center gap-4 mt-9"
          >
            <BuyButton location="hero" size="lg" testId="buy-btn-hero" />
          </motion.div>

          {/* Growing Circle CTA in the hero. Only shown on true phone
              widths (<md). From md upwards the header already carries the
              button, so this duplicate is hidden to avoid redundancy. */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.35, duration: 0.8 }}
            className="md:hidden mt-4"
          >
            <Link
              to="/growing-circle"
              data-testid="growing-circle-btn-hero-mobile"
              onClick={() => trackCircleCta("hero_mobile")}
              className="inline-flex items-center gap-2 px-5 py-3 rounded-full font-label text-[0.62rem] tracking-[0.18em] uppercase whitespace-nowrap ring-1 ring-inset ring-[rgba(122,30,47,0.85)] text-[#f5ecdf] transition-all duration-300 shadow-[0_2px_10px_-3px_rgba(122,30,47,0.55)]"
              style={{
                backgroundColor: "rgba(122,30,47,0.88)",
                backgroundImage:
                  "linear-gradient(180deg, rgba(154,42,63,0.95) 0%, rgba(90,26,36,0.95) 100%)",
              }}
            >
              <span>Self Doubling Growing Circle</span>
              <ArrowUpRight size={13} strokeWidth={1.75} aria-hidden className="-mr-0.5 opacity-90" />
            </Link>
          </motion.div>

          <p className="font-label text-[0.65rem] text-muted mt-6 tracking-[0.14em]">
            BY MARIA GIOIA MOSCA · {BOOK.period}
          </p>
        </motion.div>

        {/* Photograph */}
        <div className="lg:col-span-5 order-1 lg:order-2 flex justify-center">
          <motion.figure
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1.3, ease: [0.22, 1, 0.36, 1], delay: 0.3 }}
            style={reduce ? {} : { y: coverY, opacity: coverOpacity }}
            className="relative w-[85vw] max-w-[440px] sm:max-w-[500px] lg:max-w-[560px] aspect-[4/5]"
            data-testid="hero-photograph"
          >
            <div
              className="absolute -inset-6 rounded-2xl blur-2xl opacity-40 pointer-events-none"
              style={{ background: "radial-gradient(circle, rgba(170,134,86,0.5), transparent 70%)" }}
              aria-hidden
            />
            <img
              src="/selfdoubling-book-held.jpeg"
              alt="Maria Gioia Mosca holding the Self Doubling book — The Truth That Dwells Within You"
              width={848}
              height={1264}
              fetchPriority="high"
              className="relative block w-full h-full rounded-md shadow-[0_40px_80px_-20px_rgba(0,0,0,0.9)] ring-1 ring-[var(--gold-line)] object-cover"
              style={{ objectPosition: "center 68%" }}
              data-testid="hero-photo-img"
            />
            <figcaption
              className="absolute bottom-0 left-0 right-0 bg-black/70 backdrop-blur-[2px] border-t border-[var(--gold-line)] px-4 py-4 sm:px-7 sm:py-5 lg:px-8 lg:py-6 rounded-b-md"
              data-testid="hero-quote-caption"
            >
              <p className="font-editorial italic text-cream text-base sm:text-xl lg:text-2xl leading-snug text-center">
                “This is the deepest embrace you can give yourself. Understand it. <span className="whitespace-nowrap">Experience it.</span>”
              </p>
            </figcaption>
          </motion.figure>
        </div>
      </div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.5 }}
        className="absolute bottom-6 left-1/2 -translate-x-1/2 font-label text-[0.6rem] tracking-[0.3em] text-muted"
      >
        SCROLL
      </motion.div>
    </section>
  );
}
